import React from 'react';
import ReactDOM from 'react-dom';
import { routerMiddleware, ConnectedRouter } from 'react-router-redux';

import { createStore, applyMiddleware } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import { Provider } from 'react-redux';
import thunk from 'redux-thunk';
import { PersistGate } from 'redux-persist/integration/react';
import { persistStore } from 'redux-persist';
import queryString from 'query-string';

import './polyfills';

import getCurrentOrganizationId, { getDefaultOrganizationId } from './utils/getCurrentOrganizationId';
import { normalizeFilters } from './utils/normalizeFilters';
import createReducers from './store/reducers';
import { getViewerCustomizations, getUserProfile } from './ajax/endpoints';

import MapViewer from './MapViewer';
import history from './history';

import './index.css';
import { LAZY_DEFAULTS, DEFAULT_ZOOM_LEVEL } from './constants';

const loadViewerCustomizations = () => {
  const organizationId = getCurrentOrganizationId();
  return getViewerCustomizations(organizationId)
    .catch(err => {
      window.console.warn('Failed to load viewer customizations:', err);
      return {
        layers: {
          profile: [],
          custom: [],
        },
        viewer: {},
      };
    })
    .then(rawCustomizations => {
      return {
        ...rawCustomizations,
        organizationId,
      };
    });
};

const createStoreAndInjectCustomizations = ([rawCustomizations, profile]) => {
  const {
    organizationId,
    layers,
    viewer: customizations,
    adminSettings,
    areaSettings,
    buildingSettings,
    siteSettings,
    markers,
    shapeError,
    suppressWarnings,
  } = rawCustomizations;
  const defaults = {
    logo: '',
    activeColor: '#f60000',
    backgroundColor: '#4a4a4a',
    defaultZoom: DEFAULT_ZOOM_LEVEL,
    landingPage: '',
    visibleSections: {
      buildings: true,
      sites: true,
      areas: true,
      folder: true,
    },
    showSearchAndAppliedFilters: true,
    activeOnLoad: 'buildings',
    searchOpen: '0',
    mapType: 'street',
    initialResults: LAZY_DEFAULTS.INITIAL,
    shapeError: '',
    suppressWarnings: false,
  };
  const viewer = {
    ...defaults,
    ...customizations,
    layers,
    adminSettings: adminSettings || {},
    areaSettings: areaSettings || {},
    buildingSettings: buildingSettings || {},
    siteSettings: siteSettings || {},
    markers: markers || {},
    shapeError: shapeError || '',
    suppressWarnings: suppressWarnings || false,
  };

  let filterDisplay = '';
  if (viewer.searchOpen === '1') {
    const searchType = window.location.pathname.split('/')[1] || viewer.activeOnLoad;
    if (['buildings', 'sites'].indexOf(searchType) > -1) {
      filterDisplay = searchType;
    }
  }

  const { filtersAndSort, other } = normalizeFilters(window.__INITIAL__);
  const query = queryString.parse(window.location.search);
  if (query.hideBuildingsAndSitesTabs || other.hideBuildingsAndSitesTabs) {
    viewer.visibleSections.buildings = viewer.visibleSections.sites = false;
  }
  if (query.hideSpecialtyFeatureMapMarkers || other.hideSpecialtyFeatureMapMarkers) {
    viewer.hideSpecialtyFeatureMapMarkers = true;
  }
  if (query.hideSearchAndAppliedFilters || other.hideSearchAndAppliedFilters) {
    viewer.showSearchAndAppliedFilters = false;
  }

  // Migrate data which was stored in redux-persist v4 to a format compatible with v5
  // In v4, data was stored as objects. In v5, it's strings.
  const favoritesKey = `reduxPersist-${organizationId}favorites`;
  const favorites = localStorage.getItem(favoritesKey);
  if (favorites) {
    const { folders, dontShow } = JSON.parse(favorites);
    if (typeof folders === 'object') {
      const filteredFolders = folders.savedFolders.filter(f => f.id && f.buildings && f.sites);
      localStorage.setItem(favoritesKey, JSON.stringify({
        folders: JSON.stringify({ ...folders, savedFolders: filteredFolders }),
        dontShow,
      }));
    } else if (typeof folders === 'string') {
      const parsedFolders = JSON.parse(folders);
      const filteredFolders = parsedFolders
        .savedFolders.filter(f => f.id && f.buildings && f.sites);
      if (filteredFolders.length !== parsedFolders.length) {
        localStorage.setItem(favoritesKey, JSON.stringify({
          folders: JSON.stringify({ ...parsedFolders, savedFolders: filteredFolders }),
          dontShow,
        }));
      }
    }
  } else { // set new session for analytics
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'endSession' });
  }

  const analytics = () => next => action => {
    // Not sure what this is for
    if (action.type === 'persist/PERSIST') {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ organizationId: getCurrentOrganizationId().substring(0, 24) });
    }

    /**
     * I don't think this is needed with GA4, leaving it in place for now
     * basicPageView Tag event pushes a `page_view` event to GA4, if you see duplicates remove this
     */
    if (
      action.type === '@@router/LOCATION_CHANGE'
      && (
        !action.payload.pathname.match(/(?=[a-f\d]{24}$)(\d+[a-f]|[a-f]+\d)/i) // match ObjectID
        // || action.payload.pathname.match(/areas/)
      )) {
      window.dataLayer.push({ propertyView: undefined, event: 'basicPageView', areaView: undefined });
    }

    if (action.type === 'ADD_ACTIVE_SITE' || action.type === 'ADD_ACTIVE_BUILDING') {
      const path = window.location.pathname.split('/');
      if ((path[1] === 'sites' || path[1] === 'buildings') && path[2]) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'propertyView',
          propertyViewDetails: {
            organizationId: getCurrentOrganizationId().substring(0, 24),
            propertyId: path[2].substring(0, 24),
            propertyType: path[1] === 'sites' ? 'site' : 'building',
            viewDatetime: new Date(),
          },
        });
      }
    }

    if (action.type === 'ADD_ACTIVE_AREA') {
      if (action.area) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'areaView',
          areaViewDetails: {
            organizationId: getCurrentOrganizationId().substring(0, 24),
            areaId: action.area._id,
            areaType: action.layer,
            viewDatetime: new Date(),
          },
        });
      }
    }
    return next(action);
  };

  const enhancer = composeWithDevTools(
    applyMiddleware(analytics, thunk, routerMiddleware(history)),
  );

  const initialState = {
    organizationId,
    viewer,
    profile,
    filterDisplay,
    ...filtersAndSort,
  };

  const store = createStore(
    createReducers(organizationId, profile.authenticated),
    initialState,
    enhancer,
  );

  const persistor = persistStore(store);

  return { store, persistor, history };
};

const renderApplication = core => {
  ReactDOM.render(
    <Provider store={core.store}>
      <PersistGate persistor={core.persistor}>
        <ConnectedRouter history={core.history}>
          <MapViewer
            organizationId={getCurrentOrganizationId().substring(0, 24)}
            defaultOrganizationId={getDefaultOrganizationId().substring(0, 24)}
          />
        </ConnectedRouter>
      </PersistGate>
    </Provider>,
    document.getElementById('LoisRoot'),
  );
};

// =========================================================================
// Application entry point
// =========================================================================
Promise.all([
  loadViewerCustomizations(),
  getUserProfile(),
])
  .then(createStoreAndInjectCustomizations)
  .then(renderApplication)
  .catch(fatalError => {
    window.console.error('A fatal startup error has occurred:', fatalError);
  });



// WEBPACK FOOTER //
// ./src/index.js